import React from "react";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import InputField from "components/common/Input/Input";
import SelectOption from "components/common/Select/Select";
import FieldHelpIcon from "components/common/FieldHelpIcon";

const RATE_MAX_WHOLE_DIGITS = 8;
const RATE_MAX_VALUE = 99999999.99;

const sanitizeRateInput = (raw) => {
  if (raw === "" || raw == null) return "";
  let value = String(raw).replace(/[^0-9.]/g, "");
  const dotIndex = value.indexOf(".");
  if (dotIndex !== -1) {
    value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1).replace(/\./g, "");
  }
  const hasDot = value.includes(".");
  const [whole = "", dec = ""] = value.split(".");
  const limitedWhole = whole.slice(0, RATE_MAX_WHOLE_DIGITS);
  const limitedDec = dec.slice(0, 2);
  if (hasDot) {
    return dec.length > 0 || value.endsWith(".") ? `${limitedWhole}.${limitedDec}` : `${limitedWhole}.`;
  }
  return limitedWhole;
};

const isValidRateFormat = (value) => {
  const str = String(value).trim();
  if (str === "") return true;
  const normalized = str.endsWith(".") ? str.slice(0, -1) : str;
  if (normalized === "") return true;
  return /^\d{1,8}(\.\d{1,2})?$/.test(normalized);
};

const requiredRate = (value) => {
  if (value === "" || value === undefined || value === null) return "Rate is required";
  const n = parseFloat(value);
  if (Number.isNaN(n)) return "Rate must be a valid number";
  if (n < 0) return "Rate must be 0 or greater";
  if (!isValidRateFormat(value)) {
    return `Rate must be at most ${RATE_MAX_VALUE.toLocaleString()} (8 digits before decimal, 2 after).`;
  }
  if (n > RATE_MAX_VALUE) {
    return `Rate must not exceed ${RATE_MAX_VALUE.toLocaleString()}.`;
  }
  return true;
};

const LINE_ITEM_CODE_EXAMPLE_BY_FUNDING = {
  NDIS: "NDIS-001",
  AGED_CARE: "AC-001",
  PRIVATE: "PVT-001",
  OTHER: "OTHER-001",
};

const lineItemCodeExample = (fundingType) =>
  LINE_ITEM_CODE_EXAMPLE_BY_FUNDING[fundingType] ?? LINE_ITEM_CODE_EXAMPLE_BY_FUNDING.NDIS;

const ServiceLineItemModal = ({
  isOpen,
  onClose,
  editingItem,
  submitting,
  submitError,
  fieldErrors,
  errors,
  handleSubmit,
  onSubmit,
  register,
  setValue,
  watchRate,
  fundingTypes,
  formFundingTypeSelectedOption,
  formCategoryDropdownData,
  formCategorySelectedOption,
}) => {
  const fundingType = formFundingTypeSelectedOption?.value ?? "NDIS";
  const codeExample = lineItemCodeExample(fundingType);
  const codePlaceholder = `e.g. ${codeExample}`;

  const modalFooter = () => (
    <div className='flex space-x-2'>
      <CustomButton
        type='button'
        label='Cancel'
        clickHandler={onClose}
        variant='secondary'
        isDisabled={submitting}
      />
      <CustomButton
        type='button'
        label={editingItem ? "Update" : "Create"}
        clickHandler={handleSubmit(onSubmit)}
        showLoading={submitting}
        isDisabled={submitting}
      />
    </div>
  );

  if (!isOpen) return null;

  return (
    <ModalSkeleton
      isOpen={isOpen}
      title={editingItem ? "Edit Service Line Item" : "Add Service Line Item"}
      closeModal={submitting ? () => {} : onClose}
      cssClass='w-[35%]'
      modalFooter={modalFooter()}
    >
      <p className='text-sm text-gray-500 mb-4'>
        {editingItem
          ? "Update the line item details below."
          : "Please enter the following details to create a new service line item."}
      </p>
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
        {submitError && (
          <div className='p-2 rounded bg-red-50 text-red-700 text-sm'>{submitError}</div>
        )}
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Funding type
          </label>
          <SelectOption
            data={fundingTypes}
            selectedOption={formFundingTypeSelectedOption}
            handleChange={(opt) => {
              if (!editingItem) setValue("funding_type", opt?.value ?? "NDIS");
            }}
            isDisabled={!!editingItem}
            useMenuPortal
            cssClass='z-10'
          />
          {editingItem && (
            <p className='text-xs text-gray-500 mt-0.5'>
              Funding type cannot be changed once the line item code has been allocated.
            </p>
          )}
          {fieldErrors.funding_type && (
            <p className='text-red-600 text-xs mt-1'>{fieldErrors.funding_type[0]}</p>
          )}
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1 flex items-center'>
            Service type <span className='text-red-500'>*</span>
            <FieldHelpIcon
              tooltipId='custom-line-item-service-category-help'
              content={
                <div className='text-left text-xs text-white max-w-xs p-1'>
                  Required for all line items. Assign to a service type (e.g. Improved Daily Living, Personal Care). Create service types using &quot;Add service type&quot; above.
                </div>
              }
            />
          </label>
          <SelectOption
            key={`${formCategorySelectedOption?.value ?? ""}-${formCategoryDropdownData.map((o) => o.value).join("-")}`}
            data={formCategoryDropdownData}
            selectedOption={formCategorySelectedOption}
            handleChange={(opt) => setValue("invoice_service_type_id", opt?.value ?? "")}
            useMenuPortal
            cssClass='z-10'
          />
          {(fieldErrors.invoice_service_type_id || errors.invoice_service_type_id) && (
            <p className='text-red-600 text-xs mt-1'>
              {fieldErrors.invoice_service_type_id?.[0] || errors.invoice_service_type_id?.message}
            </p>
          )}
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1 flex items-center'>
            Code {!editingItem && <span className='text-red-500'>*</span>}
            <FieldHelpIcon
              tooltipId='custom-line-item-code-help'
              content={
                editingItem
                  ? "This code is used on invoices and timesheets."
                  : `Enter a unique code for this line item (for example ${codeExample}). It must be unique within your organisation and appears on invoices and timesheets.`
              }
            />
          </label>
          {editingItem ? (
            <>
              <div className='py-1.5 text-sm text-gray-900 font-medium'>{editingItem.unique_code}</div>
              <p className='text-xs text-gray-500 mt-0.5'>Code cannot be changed.</p>
            </>
          ) : (
            <>
              <InputField
                register={register}
                setValue={setValue}
                name='unique_code'
                type='text'
                placeHolder={codePlaceholder}
                autoComplete='off'
                isRequired
                maxLength={50}
                ErrorMessage={fieldErrors.unique_code?.[0] || errors.unique_code?.message}
              />
              <p className='text-xs text-gray-500 mt-0.5'>Must be unique within your organisation (max 50 characters).</p>
            </>
          )}
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1 flex items-center'>
            Description
            <FieldHelpIcon
              tooltipId='custom-line-item-description-help'
              content='A clear name or description of the support or service. Shown on invoices and timesheets.'
            />
          </label>
          <textarea
            {...register("description", { required: "Description is required" })}
            rows={2}
            className='mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md resize-none'
          />
          {(fieldErrors.description || errors.description) && (
            <p className='text-red-600 text-xs mt-1'>
              {fieldErrors.description?.[0] || errors.description?.message}
            </p>
          )}
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1 flex items-center'>
            Rate ($)
            <FieldHelpIcon
              tooltipId='custom-line-item-rate-help'
              content='Unit charge-out rate in dollars (e.g. per hour)—used for invoicing participants or funders.'
            />
          </label>
          <input
            type='hidden'
            {...register("rate", { validate: requiredRate, required: "Rate is required." })}
          />
          <InputField
            name='rate'
            type='number'
            placeHolder='0.00'
            value={watchRate ?? ""}
            handleChange={(val) => setValue("rate", sanitizeRateInput(val), { shouldValidate: true })}
            min={0}
            max={RATE_MAX_VALUE}
            step='0.01'
            ErrorMessage={fieldErrors.rate?.[0] || errors.rate?.message}
          />
        </div>
      </form>
    </ModalSkeleton>
  );
};

export default ServiceLineItemModal;
